/**
 * The saved-lists endpoints (`/api/3.0/profile/list…`), as plain requests.
 *
 * Same-origin, session cookie, root-relative paths — no `credentials`, no CORS. Every
 * non-GET carries the CSRF token as a **root-level `__csrfToken` body field**, JSON
 * encoded, taken from the shell's single domain configuration. Deliberately **no
 * DPoP**: these are account data, not a device-bound capability, so `dpopFetch` is not
 * used here (see documentation/spec-user-lists-api.md, decision 6).
 *
 * This module knows nothing about React or about the shared manifest — it is the
 * transport, and `store.ts` is the state.
 */
import { loadDomainConfiguration } from '../config/configApi'
import { LIST_NAMES } from './types'
import type { ListItem, ListManifest, ListName, ListPage, ListPageOptions } from './types'

const MANIFEST_URL = '/api/3.0/profile/lists'
const listUrl = (list: ListName): string => `/api/3.0/profile/list/${list}`

/** The API's own default page size; sent explicitly so the client controls it. */
export const LIST_PAGE_SIZE = 40

/** The API's cap. Asking for more is a client bug, not a bigger page. */
export const LIST_PAGE_SIZE_MAX = 100

/**
 * A non-2xx answer, carrying the status so callers can tell the cases apart — `401`
 * (signed out) from `404` (unknown title) from everything else. Without this the
 * status is lost in a generic Error and every failure looks the same.
 */
export class ListRequestError extends Error {
  readonly status: number

  constructor (status: number, message: string) {
    super(message)
    this.name = 'ListRequestError'
    this.status = status
  }
}

/** True for the statuses that mean "this viewer has no lists", not "this broke". */
export const isSignedOut = (error: unknown): boolean =>
  error instanceof ListRequestError && (error.status === 401 || error.status === 403)

/**
 * The membership manifest. Complete and never truncated — a partial manifest would
 * render the wrong toggle state on the titles it omitted.
 *
 * Normalised into both keys always being arrays: the API promises that, and a toggle
 * reading `manifest[list]` must never have to guard.
 */
export async function fetchListManifest (): Promise<ListManifest> {
  const body = await request<Partial<Record<ListName, unknown>>>(MANIFEST_URL, { method: 'GET' })
  const manifest = {} as ListManifest
  LIST_NAMES.forEach(list => {
    const ids = body?.[list]
    manifest[list] = Array.isArray(ids) ? ids.map(String) : []
  })
  return manifest
}

/**
 * One page of resolved titles. `limit` is the name this API uses (as `movie/discover`
 * does), and unlike the catalogue endpoints its counters are exact and `total_pages`
 * rounds up — so a caller may drive its paging from `has_more` rather than inferring
 * it from a full page.
 */
export function fetchListPage (list: ListName, options: ListPageOptions = {}): Promise<ListPage> {
  const page = Math.max(1, Math.trunc(options.page ?? 1))
  const limit = Math.min(LIST_PAGE_SIZE_MAX, Math.max(1, Math.trunc(options.limit ?? LIST_PAGE_SIZE)))
  return request<ListPage>(`${listUrl(list)}?page=${page}&limit=${limit}`, { method: 'GET' })
}

/**
 * Save a title. Answers with the created item, so a caller can render it without a
 * refetch. Idempotent: a double-tap is a `200`, never a conflict, and never a
 * duplicate.
 */
export async function addListItem (list: ListName, movieId: string): Promise<ListItem> {
  const { csrf_token: csrfToken } = await loadDomainConfiguration()
  return request<ListItem>(listUrl(list), {
    method: 'POST',
    body: { __csrfToken: csrfToken, item: { movie: movieId } },
  })
}

/**
 * Remove a title. `204`, and `204` again for a title that was not in the list — a
 * toggle that turns something off twice has got what it asked for. The JSON body on a
 * DELETE is the house pattern here (`profile/device/{id}`).
 */
export async function removeListItem (list: ListName, movieId: string): Promise<void> {
  const { csrf_token: csrfToken } = await loadDomainConfiguration()
  await request<null>(`${listUrl(list)}/${encodeURIComponent(movieId)}`, {
    method: 'DELETE',
    body: { __csrfToken: csrfToken },
  })
}

/*
 * Helpers
 */

interface RequestOptions {
  method: 'GET' | 'POST' | 'DELETE';
  body?: unknown;
}

/**
 * One request, one place to turn a non-2xx into a {@link ListRequestError} — otherwise
 * an error body flows into `.json()` and surfaces as a confusing parse error three
 * frames away from the status that actually explains it.
 */
async function request<T> (url: string, options: RequestOptions): Promise<T> {
  const res = await fetch(url, {
    method: options.method,
    headers: options.body === undefined
      ? { Accept: 'application/json' }
      : { Accept: 'application/json', 'Content-Type': 'application/json' },
    body: options.body === undefined ? undefined : JSON.stringify(options.body),
  })
  if (!res.ok) {
    throw new ListRequestError(res.status, `${options.method} ${url} failed (${res.status})`)
  }
  // `204` on DELETE, and any empty body: nothing to parse, and callers of those
  // endpoints have nothing to read.
  if (res.status === 204) return null as T
  return res.json() as Promise<T>
}
